import type { Transaction } from "@rocicorp/zero";
import { nanoid } from "nanoid";
import * as z from "zod";
import type { UserAction } from "../aiSdkMessages";
import { zql } from "../generatedDrizzleSchema";
import { type AttributionCtx, actorColumns } from "../rbac/roles";
import { stripNullBytesFromRecord } from "../stripNullBytes";

export { makeBranchParams } from "../copyOnWrite";

export const generateId = () => nanoid(12);

export const branchSchema = z.object({
  branchId: z.string(),
  forkBranchId: z.string(),
  forkCommitId: z.string(),
  forkChatId: z.string(),
  workingCommitId: z.string(),
  workingChatId: z.string(),
});

// Values can carry uploaded file bytes (e.g. a connector CA certificate), and
// Postgres rejects null bytes in jsonb.
export const stringRecord = z
  .record(z.string(), z.string())
  .transform(stripNullBytesFromRecord);

export async function insertUserAction(
  tx: Transaction,
  commitId: string,
  ctx: AttributionCtx,
  tenantId: string,
  actionType: UserAction["actionType"],
  metadata: UserAction["metadata"]
) {
  // user_actions is a human audit trail with no service-account column, so a
  // pure service account records no marker.
  const { userId } = actorColumns(ctx);
  if (!userId) return;
  const [chat] = await tx.run(zql.chats.where("commitId", commitId));
  if (!chat) return;

  const lastEntries = await tx.run(
    zql.chatEntries
      .where("chatId", chat.id)
      .orderBy("createdAt", "desc")
      .limit(1)
  );

  const entryId = generateId();
  const now = Date.now();

  await tx.mutate.chatEntries.insert({
    id: entryId,
    chatId: chat.id,
    entryType: "UserAction",
    createdAt: now,
    previousEntryId: lastEntries[0]?.id ?? null,
    tenantId,
  });

  await tx.mutate.userActions.insert({
    entryId,
    userId,
    actionType,
    metadata,
    tenantId,
  });
}
